/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteTagPathParameters,
  GetTagPathParameters,
  ListTagsParams,
  TLCreateTagRequest,
  TLHTTPValidationError,
  TLPaginatedResponseTagResponse,
  TLTagResponse,
  TLUpdateTagRequest,
  UpdateTagPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Tags
 */
export const listTags = (
  params?: ListTagsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseTagResponse>({ url: `/kb/tags`, method: 'GET', params, signal }, options);
};

export const getListTagsQueryKey = (params?: ListTagsParams) => {
  return ['kb', 'tags', ...(params ? [params] : [])] as const;
};

export const getListTagsQueryOptions = <
  TData = Awaited<ReturnType<typeof listTags>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTagsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTags>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTagsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listTags>>> = ({ signal }) =>
    listTags(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listTags>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListTagsQueryResult = NonNullable<Awaited<ReturnType<typeof listTags>>>;
export type ListTagsQueryError = ErrorType<TLHTTPValidationError>;

export function useListTags<TData = Awaited<ReturnType<typeof listTags>>, TError = ErrorType<TLHTTPValidationError>>(
  params: undefined | ListTagsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTags>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof listTags>>, TError, Awaited<ReturnType<typeof listTags>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListTags<TData = Awaited<ReturnType<typeof listTags>>, TError = ErrorType<TLHTTPValidationError>>(
  params?: ListTagsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTags>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof listTags>>, TError, Awaited<ReturnType<typeof listTags>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListTags<TData = Awaited<ReturnType<typeof listTags>>, TError = ErrorType<TLHTTPValidationError>>(
  params?: ListTagsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTags>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Tags
 */

export function useListTags<TData = Awaited<ReturnType<typeof listTags>>, TError = ErrorType<TLHTTPValidationError>>(
  params?: ListTagsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTags>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListTagsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Tags
 */
export const prefetchListTagsQuery = async <
  TData = Awaited<ReturnType<typeof listTags>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListTagsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTags>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListTagsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Tags
 */
export const invalidateListTags = async (
  queryClient: QueryClient,
  params?: ListTagsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListTagsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Tag
 */
export const createTag = (
  tLCreateTagRequest: BodyType<TLCreateTagRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTagResponse>(
    {
      url: `/kb/tags`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCreateTagRequest,
      signal,
    },
    options,
  );
};

export const getCreateTagQueryKey = (tLCreateTagRequest?: BodyType<TLCreateTagRequest>) => {
  return ['POST', 'kb', 'tags', tLCreateTagRequest] as const;
};

export const getCreateTagQueryOptions = <
  TData = Awaited<ReturnType<typeof createTag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateTagRequest: BodyType<TLCreateTagRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateTagQueryKey(tLCreateTagRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createTag>>> = ({ signal }) =>
    createTag(tLCreateTagRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createTag>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateTagQueryResult = NonNullable<Awaited<ReturnType<typeof createTag>>>;
export type CreateTagQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateTag<TData = Awaited<ReturnType<typeof createTag>>, TError = ErrorType<TLHTTPValidationError>>(
  tLCreateTagRequest: BodyType<TLCreateTagRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTag>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof createTag>>, TError, Awaited<ReturnType<typeof createTag>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateTag<TData = Awaited<ReturnType<typeof createTag>>, TError = ErrorType<TLHTTPValidationError>>(
  tLCreateTagRequest: BodyType<TLCreateTagRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTag>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createTag>>,
          TError,
          Awaited<ReturnType<typeof createTag>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateTag<TData = Awaited<ReturnType<typeof createTag>>, TError = ErrorType<TLHTTPValidationError>>(
  tLCreateTagRequest: BodyType<TLCreateTagRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Tag
 */

export function useCreateTag<TData = Awaited<ReturnType<typeof createTag>>, TError = ErrorType<TLHTTPValidationError>>(
  tLCreateTagRequest: BodyType<TLCreateTagRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateTagQueryOptions(tLCreateTagRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Tag
 */
export const prefetchCreateTagQuery = async <
  TData = Awaited<ReturnType<typeof createTag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLCreateTagRequest: BodyType<TLCreateTagRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateTagQueryOptions(tLCreateTagRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Tag
 */
export const invalidateCreateTag = async (
  queryClient: QueryClient,
  tLCreateTagRequest: BodyType<TLCreateTagRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateTagQueryKey(tLCreateTagRequest) }, options);

  return queryClient;
};

/**
 * @summary Delete Tag
 */
export const deleteTag = (
  { tagId }: DeleteTagPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/kb/tags/${tagId}`, method: 'DELETE', signal }, options);
};

export const getDeleteTagQueryKey = ({ tagId }: DeleteTagPathParameters) => {
  return ['DELETE', 'kb', 'tags', tagId] as const;
};

export const getDeleteTagQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteTag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tagId }: DeleteTagPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteTagQueryKey({ tagId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteTag>>> = ({ signal }) =>
    deleteTag({ tagId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: tagId !== null && tagId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof deleteTag>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DeleteTagQueryResult = NonNullable<Awaited<ReturnType<typeof deleteTag>>>;
export type DeleteTagQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteTag<TData = Awaited<ReturnType<typeof deleteTag>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: DeleteTagPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTag>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof deleteTag>>, TError, Awaited<ReturnType<typeof deleteTag>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteTag<TData = Awaited<ReturnType<typeof deleteTag>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: DeleteTagPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTag>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteTag>>,
          TError,
          Awaited<ReturnType<typeof deleteTag>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteTag<TData = Awaited<ReturnType<typeof deleteTag>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: DeleteTagPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Tag
 */

export function useDeleteTag<TData = Awaited<ReturnType<typeof deleteTag>>, TError = ErrorType<TLHTTPValidationError>>(
  { tagId }: DeleteTagPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteTagQueryOptions({ tagId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Tag
 */
export const prefetchDeleteTagQuery = async <
  TData = Awaited<ReturnType<typeof deleteTag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { tagId }: DeleteTagPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteTagQueryOptions({ tagId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Tag
 */
export const invalidateDeleteTag = async (
  queryClient: QueryClient,
  { tagId }: DeleteTagPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteTagQueryKey({ tagId }) }, options);

  return queryClient;
};

/**
 * @summary Get Tag
 */
export const getTag = (
  { tagId }: GetTagPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTagResponse>({ url: `/kb/tags/${tagId}`, method: 'GET', signal }, options);
};

export const getGetTagQueryKey = ({ tagId }: GetTagPathParameters) => {
  return ['kb', 'tags', tagId] as const;
};

export const getGetTagQueryOptions = <
  TData = Awaited<ReturnType<typeof getTag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tagId }: GetTagPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTagQueryKey({ tagId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTag>>> = ({ signal }) =>
    getTag({ tagId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: tagId !== null && tagId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getTag>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTagQueryResult = NonNullable<Awaited<ReturnType<typeof getTag>>>;
export type GetTagQueryError = ErrorType<TLHTTPValidationError>;

export function useGetTag<TData = Awaited<ReturnType<typeof getTag>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetTagPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTag>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof getTag>>, TError, Awaited<ReturnType<typeof getTag>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTag<TData = Awaited<ReturnType<typeof getTag>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetTagPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTag>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof getTag>>, TError, Awaited<ReturnType<typeof getTag>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTag<TData = Awaited<ReturnType<typeof getTag>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetTagPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Tag
 */

export function useGetTag<TData = Awaited<ReturnType<typeof getTag>>, TError = ErrorType<TLHTTPValidationError>>(
  { tagId }: GetTagPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetTagQueryOptions({ tagId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Tag
 */
export const prefetchGetTagQuery = async <
  TData = Awaited<ReturnType<typeof getTag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { tagId }: GetTagPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetTagQueryOptions({ tagId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Tag
 */
export const invalidateGetTag = async (
  queryClient: QueryClient,
  { tagId }: GetTagPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetTagQueryKey({ tagId }) }, options);

  return queryClient;
};

/**
 * @summary Update Tag
 */
export const updateTag = (
  { tagId }: UpdateTagPathParameters,
  tLUpdateTagRequest: BodyType<TLUpdateTagRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTagResponse>(
    {
      url: `/kb/tags/${tagId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLUpdateTagRequest,
      signal,
    },
    options,
  );
};

export const getUpdateTagQueryKey = (
  { tagId }: UpdateTagPathParameters,
  tLUpdateTagRequest?: BodyType<TLUpdateTagRequest>,
) => {
  return ['PUT', 'kb', 'tags', tagId, tLUpdateTagRequest] as const;
};

export const getUpdateTagQueryOptions = <
  TData = Awaited<ReturnType<typeof updateTag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tagId }: UpdateTagPathParameters,
  tLUpdateTagRequest: BodyType<TLUpdateTagRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateTagQueryKey({ tagId }, tLUpdateTagRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateTag>>> = ({ signal }) =>
    updateTag({ tagId }, tLUpdateTagRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: tagId !== null && tagId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof updateTag>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UpdateTagQueryResult = NonNullable<Awaited<ReturnType<typeof updateTag>>>;
export type UpdateTagQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateTag<TData = Awaited<ReturnType<typeof updateTag>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: UpdateTagPathParameters,
  tLUpdateTagRequest: BodyType<TLUpdateTagRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTag>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof updateTag>>, TError, Awaited<ReturnType<typeof updateTag>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateTag<TData = Awaited<ReturnType<typeof updateTag>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: UpdateTagPathParameters,
  tLUpdateTagRequest: BodyType<TLUpdateTagRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTag>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateTag>>,
          TError,
          Awaited<ReturnType<typeof updateTag>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateTag<TData = Awaited<ReturnType<typeof updateTag>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: UpdateTagPathParameters,
  tLUpdateTagRequest: BodyType<TLUpdateTagRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Tag
 */

export function useUpdateTag<TData = Awaited<ReturnType<typeof updateTag>>, TError = ErrorType<TLHTTPValidationError>>(
  { tagId }: UpdateTagPathParameters,
  tLUpdateTagRequest: BodyType<TLUpdateTagRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateTagQueryOptions({ tagId }, tLUpdateTagRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Tag
 */
export const prefetchUpdateTagQuery = async <
  TData = Awaited<ReturnType<typeof updateTag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { tagId }: UpdateTagPathParameters,
  tLUpdateTagRequest: BodyType<TLUpdateTagRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateTagQueryOptions({ tagId }, tLUpdateTagRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Tag
 */
export const invalidateUpdateTag = async (
  queryClient: QueryClient,
  { tagId }: UpdateTagPathParameters,
  tLUpdateTagRequest: BodyType<TLUpdateTagRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getUpdateTagQueryKey({ tagId }, tLUpdateTagRequest) }, options);

  return queryClient;
};
